import type { MapGradientPoint } from "@/stores/mapStore";
import { decodePolyline } from "@/utils/polylineUtils";
import type { ItineraryDestination, ItineraryStep } from "../types";

export interface ItineraryCoreOptions {
  steps?: ItineraryStep[];
  destination?: ItineraryDestination;
}

export abstract class ItineraryCore {
  steps: ItineraryStep[];
  destination: ItineraryDestination | null;

  constructor(options: ItineraryCoreOptions = {}) {
    // check passed steps are formatted correctly
    if (options?.steps) {
      if (!Array.isArray(options.steps)) {
        throw new Error("Steps must be an array of ItineraryStep objects");
      }
      if (options.steps.some((step) => typeof step !== "object")) {
        throw new Error("Steps must be an array of ItineraryStep objects");
      }
      if (
        options.steps.some((step) => step.polyline !== "" && !step.polyline)
      ) {
        throw new Error("Each step must have a polyline");
      }
    }

    // check passed destination is formatted correctly
    if (options?.destination) {
      if (typeof options.destination !== "object") {
        throw new Error("Destination must be an ItineraryDestination object");
      }
      if (typeof options.destination.name !== "string") {
        throw new Error("Destination name must be a string");
      }
      if (typeof options.destination.addressStr !== "string") {
        throw new Error("Destination address string must be a string");
      }
    }

    this.steps = options.steps || [];
    this.destination = options.destination || null;
  }

  // ----------------------------------------------------------------------- //
  // -------------------------------- Getters ------------------------------ //
  // ----------------------------------------------------------------------- //

  /**
   * Checks if the ItineraryV2 instance is valid.
   *
   * @return {boolean} Returns true if the instance has at least one step and a
   * destination, otherwise false.
   */
  public get isValid(): boolean {
    return !!this.steps.length && !!this.destination;
  }

  /**
   * Calculate the total travel time spent driving for the trip.
   *
   * @return {number} The total driving time in seconds.
   */
  public get totalDrivingTime(): number {
    return this.steps.reduce((acc, step) => acc + step.drivingTime, 0);
  }

  abstract get totalStoppedTime(): number;

  /**
   * Calculate the total ferry time for the trip.
   *
   * @return {number} The total ferry time in seconds.
   */
  public get totalFerryTime(): number {
    return this.steps.reduce((acc, step) => acc + step.ferryTime, 0);
  }

  /**
   * Returns the total time for the trip in seconds.
   *
   * @return {number} The total time for the trip in seconds.
   */
  public get totalTime(): number {
    return this.totalDrivingTime + this.totalStoppedTime + this.totalFerryTime;
  }

  /**
   * Calculates the total driving distance for the trip by summing up the
   * driving distances of each step in meters.
   *
   * @return {number} The total driving distance for the trip in meters.
   */
  public get totalDrivingDistance(): number {
    return this.steps.reduce((acc, step) => acc + step.drivingDistance, 0);
  }

  /**
   * Returns the points of each leg in the trip as latitude and longitude
   * coordinate arrays.
   *
   * @return {[number, number][][]} The points of each leg in the trip.
   */
  public get pointsByLeg(): [number, number][][] {
    return this.steps.map((step) => decodePolyline(step.polyline));
  }

  abstract getGradientPointsByLeg(): MapGradientPoint[][];
}
